import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ListTodo, Loader2, CheckCircle2, XCircle, PauseCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ModulePage, Stat, Empty, BTN_GHOST, fmtTime } from "@/components/common/ModuleShell";

const STATUS = {
  running: { color: "text-jarvis-cyanBright", Icon: Loader2, spin: true },
  completed: { color: "text-jarvis-cyan", Icon: CheckCircle2 },
  failed: { color: "text-jarvis-red", Icon: XCircle },
  interrupted: { color: "text-jarvis-amber", Icon: PauseCircle },
  cancelled: { color: "text-slate-500", Icon: XCircle },
  queued: { color: "text-jarvis-amber", Icon: Loader2 },
};
const FILTERS = ["all", "running", "completed", "failed", "interrupted", "cancelled"];

function TaskRow({ t, open, onToggle }) {
  const s = STATUS[t.status] || STATUS.queued;
  return (
    <div className={cn("jv-panel p-4 cursor-pointer transition-colors hover:border-jarvis-cyan/30", open && "border-jarvis-cyan/40")} onClick={onToggle} data-testid={`task-row-${t.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm text-slate-100 leading-snug">{t.title}</div>
          <div className="font-mono text-[10px] text-slate-500 mt-1">{t.agent} · {fmtTime(t.created_at)}{t.completed_at ? ` → ${fmtTime(t.completed_at)}` : ""} · {t.command}</div>
        </div>
        <span className={cn("flex items-center gap-1 font-mono text-[10px] uppercase shrink-0", s.color)} data-testid={`task-status-${t.id}`}>
          <s.Icon className={cn("w-3.5 h-3.5", s.spin && "animate-spin")} />{t.status}
        </span>
      </div>
      <div className="mt-3 h-1 rounded-full bg-white/8 overflow-hidden"><div className="h-full bg-jarvis-cyan/70 transition-[width]" style={{ width: `${t.progress || 0}%` }} /></div>
      {open && (
        <div className="mt-3 border-t border-white/5 pt-3" data-testid={`task-detail-${t.id}`}>
          <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">Log</div>
          <pre className="font-code text-[11px] text-slate-300 whitespace-pre-wrap max-h-56 overflow-y-auto">{(t.log || []).join("\n") || "No log lines recorded."}</pre>
          {t.result && <><div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mt-3 mb-1">Result</div>
            <pre className="font-code text-[11px] text-jarvis-cyanBright/80 whitespace-pre-wrap max-h-40 overflow-y-auto">{JSON.stringify(t.result, null, 2)}</pre></>}
        </div>
      )}
    </div>
  );
}

export default function TasksPage() {
  const [tasks, setTasks] = useState(null), [filter, setFilter] = useState("all"), [open, setOpen] = useState(null);
  const navigate = useNavigate();
  const load = useCallback(() => apiGet("/tasks").then(setTasks).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); const id = setInterval(load, 3000); return () => clearInterval(id); }, [load]);
  const counts = useMemo(() => (tasks || []).reduce((acc, t) => ({ ...acc, [t.status]: (acc[t.status] || 0) + 1 }), {}), [tasks]);
  const shown = (tasks || []).filter((t) => filter === "all" || t.status === filter);
  return (
    <ModulePage icon={ListTodo} title="Tasks" testId="tasks-page"
      subtitle={tasks ? `${tasks.length} recorded app-worker jobs · ${counts.running || 0} running · general task execution not connected` : "Loading…"}
      right={<button onClick={() => navigate("/agents")} className={BTN_GHOST} data-testid="tasks-go-agents">Run a worker command →</button>}>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
        <Stat label="Running" value={counts.running || 0} testId="stat-running" />
        <Stat label="Completed" value={counts.completed || 0} testId="stat-completed" />
        <Stat label="Failed" value={counts.failed || 0} tone={counts.failed ? "red" : "muted"} testId="stat-failed" />
        <Stat label="Interrupted" value={counts.interrupted || 0} tone={counts.interrupted ? "amber" : "muted"} testId="stat-interrupted" />
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => <button key={f} onClick={() => setFilter(f)} data-testid={`task-filter-${f}`}
          className={cn("font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-md border transition-colors", filter === f ? "border-jarvis-cyan/50 text-jarvis-cyanBright bg-jarvis-cyan/10" : "border-white/10 text-slate-500 hover:text-slate-300")}>
          {f}{f !== "all" && counts[f] ? ` · ${counts[f]}` : ""}</button>)}
      </div>
      <div className="space-y-3">
        {tasks && shown.length === 0 && <Empty testId="tasks-empty">{tasks.length ? "No jobs match this filter." : "No recorded jobs yet. Jobs appear here when you run a supported command on an agent's app worker."}</Empty>}
        {shown.map((t) => <TaskRow key={t.id} t={t} open={open === t.id} onToggle={() => setOpen(open === t.id ? null : t.id)} />)}
      </div>
    </ModulePage>
  );
}
